import { useMemo, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { ChevronDown, Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UserMenu } from '@/components/ui/user-menu'
import { OddLogoHorizontal } from '@/components/common/OddLogoHorizontal'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import { SiteHeader, type SiteHeaderNavItem } from './SiteHeader'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useUserProfile } from '@/hooks/useUserProfile'
import { useUserRole } from '@/hooks/useUserRole'
import { useEvents } from '@/features/ticketing/hooks/useEvents'
import { CartIcon } from '@/features/shop'

// ─── Nav data ─────────────────────────────────────────────────────────────────

const staticNavLinks: SiteHeaderNavItem[] = [
  {
    to: '/seasons',
    label: 'Films',
    children: [
      {
        to: '/seasons',
        label: 'All Seasons',
        description: 'Every season of ODD films, from the beginning',
      },
      {
        to: '/films/projects',
        label: 'Projects',
        description: 'Client work, collaborations, and one-off films',
      },
    ],
  },
  {
    to: '/company',
    label: 'Company',
    children: [
      {
        to: '/company',
        label: 'Meet the Team',
        description: 'Dancers, choreographers, and the people behind ODD',
      },
      {
        to: '/applications',
        label: 'Applications',
        description: 'Open applications and audition information',
      },
    ],
  },
  {
    to: '/services',
    label: 'Services',
    children: [
      {
        to: '/services',
        label: 'What We Offer',
        description: 'Film production, event coverage, and workshops',
      },
      {
        to: '/request',
        label: 'Book Now',
        description: 'Submit a service request and get a quote',
      },
    ],
  },
  {
    to: '/festivals',
    label: 'Festivals',
    children: [
      {
        to: '/festivals',
        label: 'All Festivals',
        description: 'Our annual festivals and performance series',
      },
      {
        to: '/festivals/oddfest',
        label: 'ODDFEST',
        description: 'Our annual celebration of dance on film',
      },
      {
        to: '/festivals/shift',
        label: 'S.H.I.F.T.',
        description: 'A live performance series for fresh choreographic voices',
      },
    ],
  },
  {
    to: '/shop',
    label: 'Shop',
  },
]

const eventsNavItem: SiteHeaderNavItem = {
  to: '/events',
  label: 'Events',
  children: [
    {
      to: '/events',
      label: 'Upcoming Events',
      description: 'Live performances and ticketed nights',
    },
    {
      to: '/tickets/lookup',
      label: 'Look up tickets',
      description: 'Find tickets from a past order email',
    },
  ],
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PublicNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null)

  const { user } = useAuth()
  const { profile } = useUserProfile()
  const isMember = Boolean(profile?.linkedMemberId)
  const { events } = useEvents()
  const hasUpcomingEvents = useMemo(() => {
    const now = Date.now()
    return events.some((e) => e.eventDate.toMillis() >= now)
  }, [events])
  const navLinks = useMemo<SiteHeaderNavItem[]>(() => {
    // Insert Events between Festivals and Shop when there are upcoming events.
    const withEvents = hasUpcomingEvents
      ? [...staticNavLinks.slice(0, -1), eventsNavItem, staticNavLinks[staticNavLinks.length - 1]!]
      : staticNavLinks
    if (!user) return withEvents
    const myAccountChildren = [
      {
        to: '/dashboard',
        label: 'Activity',
        description: 'Your services, tickets, and shop orders',
      },
      ...(isMember
        ? [
            {
              to: '/dashboard/profile',
              label: 'My Profile',
              description: 'Edit your public company page',
            },
            {
              to: '/portal/requests',
              label: 'Service Requests',
              description: 'Matching requests plus upcoming and active bookings',
            },
            {
              to: '/portal/invoices',
              label: 'Invoices',
              description: 'Submit invoices and track approval',
            },
          ]
        : []),
      {
        to: '/dashboard/settings',
        label: 'Settings',
        description: 'Email preferences and appearance',
      },
    ]
    return [{ to: '/dashboard', label: 'My Account', children: myAccountChildren }, ...withEvents]
  }, [user, isMember, hasUpcomingEvents])
  const { isAdmin } = useUserRole()

  return (
    <SiteHeader
      className="border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      logo={
        <Link to="/" className="flex items-center">
          <OddLogoHorizontal height={44} className="text-foreground" color="currentColor" />
        </Link>
      }
      navItems={navLinks}
      rightSlot={
        <>
          {/* Desktop auth — hidden on mobile */}
          <div className="hidden items-center gap-2 xl:flex">
            <ThemeToggle />
            <CartIcon />
            {user ? (
              <UserMenu user={user} isAdmin={isAdmin} size="sm" maxNameLength={20} />
            ) : (
              <Link to="/sign-in">
                <Button size="sm" variant="outline">
                  Sign In
                </Button>
              </Link>
            )}
            <Link to="/applications">
              <Button size="sm">Apply</Button>
            </Link>
          </div>

          {/* Mobile cart + toggle — hidden on desktop */}
          <div className="flex items-center gap-1 xl:hidden">
            <ThemeToggle className="mr-1" />
            <CartIcon />
            <button onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </>
      }
      belowSlot={
        mobileOpen ? (
          <div className="border-t bg-background xl:hidden">
            <nav className="container mx-auto flex flex-col gap-1 px-4 py-4">
              {navLinks.map(({ to, label, children }) => (
                <div key={to}>
                  {children ? (
                    <>
                      <button
                        onClick={() => setExpandedMobile((v) => (v === to ? null : to))}
                        className={cn(
                          'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          expandedMobile === to
                            ? 'bg-accent text-foreground'
                            : 'text-muted-foreground hover:bg-accent'
                        )}
                      >
                        {label}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            expandedMobile === to && 'rotate-180'
                          )}
                        />
                      </button>
                      {expandedMobile === to && (
                        <div className="ml-3 mt-1 flex flex-col gap-0.5 border-l pl-3">
                          {children.map((child) => (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              onClick={() => {
                                setMobileOpen(false)
                                setExpandedMobile(null)
                              }}
                              className={({ isActive }) =>
                                cn(
                                  'block rounded-md px-3 py-2 text-sm transition-colors',
                                  isActive
                                    ? 'text-brand-orange'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                )
                              }
                            >
                              <div className="font-medium">{child.label}</div>
                              {child.description && (
                                <div className="text-xs opacity-60">{child.description}</div>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-brand-orange-light text-brand-orange-dark'
                            : 'text-muted-foreground hover:bg-accent'
                        )
                      }
                    >
                      {label}
                    </NavLink>
                  )}
                </div>
              ))}

              {user ? (
                <div className="px-3 py-2">
                  <UserMenu user={user} isAdmin={isAdmin} size="sm" maxNameLength={24} />
                </div>
              ) : (
                <Link to="/sign-in" onClick={() => setMobileOpen(false)}>
                  <Button size="sm" variant="outline" className="w-full">
                    Sign In
                  </Button>
                </Link>
              )}
              <Link to="/applications" onClick={() => setMobileOpen(false)} className="mt-2">
                <Button size="sm" className="w-full">
                  Apply
                </Button>
              </Link>
            </nav>
          </div>
        ) : null
      }
    />
  )
}
